import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { http, formatApiErrorDetail } from "../../lib/auth";

const BASE = process.env.REACT_APP_BACKEND_URL;

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await http.post("/admin/upload", fd);
  return data;
};

const Picker = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadFile(file));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className="relative flex aspect-[4/5] cursor-pointer flex-col items-center justify-center overflow-hidden border border-dashed border-neutral-300 bg-neutral-50 hover:border-neutral-950" data-testid={testid}>
      {value ? <img src={`${BASE}${value.url}`} alt={label} className="absolute inset-0 h-full w-full object-cover" /> : (
        <><Upload size={18} strokeWidth={1.25} /><span className="mt-3 text-[10px] uppercase tracking-[0.25em] text-neutral-500">{busy ? "Uploading…" : label}</span></>
      )}
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" />
    </label>
  );
};

const useGalleryAdmin = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => http.get("/gallery").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const Remove = ({ onClick, testid }) => (
  <button onClick={onClick} className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700" data-testid={testid}><Trash2 size={13} strokeWidth={1.25} /> Remove</button>
);

export const BeforeAfterTab = () => {
  const [data, load] = useGalleryAdmin();
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [treatment, setTreatment] = useState("");
  const [caption, setCaption] = useState("");
  const save = async () => {
    if (!before || !after || !treatment) return toast.error("Add both photos and the treatment name.");
    await http.post("/admin/before-after", { treatment, caption, before_path: before.path, after_path: after.path });
    toast.success("Published to the website");
    setBefore(null); setAfter(null); setTreatment(""); setCaption("");
    load();
  };
  const remove = async (id) => { await http.delete(`/admin/before-after/${id}`); toast.success("Removed"); load(); };
  return (
    <div className="space-y-10">
      <div className="bg-white p-8" data-testid="before-after-uploader">
        <p className="font-display text-2xl">Add a result</p>
        <p className="mt-1 text-sm font-light text-neutral-500">Only share photos you have client consent to publish. Until you add one, the site shows placeholders.</p>
        <div className="mt-6 grid gap-6 md:grid-cols-[200px_200px_1fr]">
          <Picker label="Before photo" value={before} onChange={setBefore} testid="upload-before" />
          <Picker label="After photo" value={after} onChange={setAfter} testid="upload-after" />
          <div className="space-y-5">
            <Input placeholder="Treatment (e.g. RF Microneedling)" value={treatment} onChange={(e) => setTreatment(e.target.value)} className="rounded-none" data-testid="ba-treatment-input" />
            <Input placeholder="Caption (e.g. Series of 3)" value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-none" data-testid="ba-caption-input" />
            <button onClick={save} className="btn-ink" data-testid="ba-save-btn">Publish</button>
          </div>
        </div>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {data?.before_after.map((p) => (
          <div key={p.id} className="bg-white p-4" data-testid={`ba-item-${p.id}`}>
            <div className="grid grid-cols-2 gap-2">
              <img src={`${BASE}${p.before_url}`} alt="Before" className="aspect-[4/5] w-full object-cover" />
              <img src={`${BASE}${p.after_url}`} alt="After" className="aspect-[4/5] w-full object-cover" />
            </div>
            <p className="font-display mt-3 text-lg">{p.treatment}</p>
            <p className="text-xs text-neutral-500">{p.caption}</p>
            <Remove onClick={() => remove(p.id)} testid={`ba-remove-${p.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const BrandTab = () => {
  const [data, load] = useGalleryAdmin();
  const [photo, setPhoto] = useState(null);
  const [caption, setCaption] = useState("");
  const save = async () => {
    if (!photo) return toast.error("Choose a photo first.");
    await http.post("/admin/brand-photos", { path: photo.path, caption });
    toast.success("Added to the homepage");
    setPhoto(null); setCaption("");
    load();
  };
  const remove = async (id) => { await http.delete(`/admin/brand-photos/${id}`); toast.success("Removed"); load(); };
  return (
    <div className="space-y-10">
      <div className="bg-white p-8" data-testid="brand-uploader">
        <p className="font-display text-2xl">Brand photos</p>
        <p className="mt-1 text-sm font-light text-neutral-500">Images from your PDF (figures wrapped in the logo line). They appear in “The Lux Veritae Muse” strip on the homepage. Save each PDF page as JPG or PNG first.</p>
        <div className="mt-6 grid gap-6 md:grid-cols-[200px_1fr]">
          <Picker label="Choose photo" value={photo} onChange={setPhoto} testid="upload-brand" />
          <div className="space-y-5">
            <Input placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-none" data-testid="brand-caption-input" />
            <button onClick={save} className="btn-ink" data-testid="brand-save-btn">Publish</button>
          </div>
        </div>
      </div>
      <div className="grid gap-6 sm:grid-cols-3 lg:grid-cols-5">
        {data?.brand.map((b) => (
          <div key={b.id} className="bg-white p-3" data-testid={`brand-item-${b.id}`}>
            <img src={`${BASE}${b.url}`} alt={b.caption} className="aspect-[3/4] w-full object-cover" />
            {b.caption && <p className="mt-2 text-sm">{b.caption}</p>}
            <Remove onClick={() => remove(b.id)} testid={`brand-remove-${b.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
};
